<template>
    <div v-if="installations.length > 0">
        <div v-if="alarmInstallations.length > 0" ref="alarmInstallations" class="vrm-block">
            <div
                class="vrm-block__header"
                sticky
                offset="40"
                sticky-class="vrm-block__header--stickyed"
            >
                <span
                    class="vrm-block__header__icon sg-vrm-icon_012_alarm-32px sg-color-red"
                ></span>
                <span>{{ $t('installationoverview.status', { i18nFallback: 'Status' }) }}</span>
                <span>
                    {{ ' ' + $t('installationoverview.alarm', { i18nFallback: 'Alarm' }) + ' ' }}
                </span>
                <span class="vrm-block__header__subtext">({{ alarmInstallations.length }})</span>
            </div>
            <div
                v-for="installation in loadedAlarmInstallations"
                :key="installation.id"
                class="vrm-block__item"
            >
                <vrm-installation-overview-item
                    :installation="installation"
                    :fade-enabled="fadeEnabled"
                ></vrm-installation-overview-item>
            </div>
            <vrm-loading v-if="!allAlarmInstallationsLoaded">
                {{ $t('common.loading', { i18nFallback: 'Loading...' }) }}
            </vrm-loading>
        </div>
        <div v-if="okInstallations.length > 0" ref="okInstallations" class="vrm-block">
            <div
                class="vrm-block__header"
                sticky
                offset="40"
                sticky-class="vrm-block__header--stickyed"
            >
                <span
                    class="vrm-block__header__icon sg-vrm-icon_054_check-16px sg-color-green"
                ></span>
                <span>{{ $t('installationoverview.status', { i18nFallback: 'Status' }) }}</span>
                <span>{{ ' ' + $t('installationoverview.ok', { i18nFallback: 'Ok' }) + ' ' }}</span>
                <span class="vrm-block__header__subtext">({{ okInstallations.length }})</span>
            </div>
            <div
                v-for="installation in loadedOkInstallations"
                :key="installation.id"
                class="vrm-block__item"
            >
                <vrm-installation-overview-item
                    :installation="installation"
                    :fade-enabled="fadeEnabled"
                ></vrm-installation-overview-item>
            </div>
            <vrm-loading v-if="!allOkInstallationsLoaded">
                {{ $t('common.loading', { i18nFallback: 'Loading...' }) }}
            </vrm-loading>
        </div>
    </div>
</template>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { AngularDirective, ngInject } from '@/vue/decorators/angular-interop';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import { LocalStorageBound } from '@/vue/decorators/localstorage';
import VrmInstallationOverviewItem from '@/vue/components/shared/VrmInstallationOverviewItem';
import VrmLoading from '@/vue/components/shared/VrmLoading.vue';

@Component({
    components: { VrmInstallationOverviewItem, VrmLoading },
})
@AngularDirective('vrm-installations-table-renderer-mobile')
@AngularLocaleBundle('installationoverview')
@AngularLocaleBundle('dashboard')
export default class VrmInstallationsTableRendererMobile extends Vue {
    @Prop()
    search;
    @Prop()
    installations;
    @Prop()
    fadeEnabled;

    @ngInject()
    $filter;

    @LocalStorageBound('vrm-installation-table-renderer.sortedColumn')
    sortedColumn = 'alarm';
    @LocalStorageBound('vrm-installation-table-renderer.sortedReverse')
    sortedReverse = false;

    alarmInstallations = [];
    okInstallations = [];
    loadedAlarmInstallations = [];
    loadedOkInstallations = [];
    loadedAlarmInstallationsCount = 10;
    loadedOkInstallationsCount = 10;
    allAlarmInstallationsLoaded = false;
    allOkInstallationsLoaded = false;

    mounted() {
        window.addEventListener('scroll', this.handleScroll);
        this.reloadInstallation();
    }

    beforeUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
    }

    @Watch('installations')
    reloadInstallation() {
        this.alarmInstallations = this.getInstallations(this.installations, true);
        this.okInstallations = this.getInstallations(this.installations, false);
        Vue.nextTick(() => {
            this.loadAlarmInstallations();
            this.loadOkInstallations();
        });
    }

    /**
     * Get filtered and sorted installations
     * @param {Array} installations
     * @param {boolean} alarm
     * @returns {Array}
     */
    getInstallations(installations, alarm = false) {
        let response = this.$filter('orderBy')(
            this.$filter('installationOverviewFilter')(installations, this.search, alarm),
            this.sortedColumn,
            this.sortedReverse,
        );

        // Sort the filtered list to show the favorites at the top.
        response = response.sort((a, b) => {
            if (a.favorite === b.favorite) return 0;
            return a.favorite ? -1 : 1;
        });

        return response;
    }

    loadAlarmInstallations() {
        this.loadedAlarmInstallations = this.alarmInstallations.slice(
            0,
            this.loadedAlarmInstallationsCount,
        );
        if (this.allAlarmInstallationsLoadedTimeout) {
            clearTimeout(this.allAlarmInstallationsLoadedTimeout);
        }
        this.allAlarmInstallationsLoadedTimeout = setTimeout(() => {
            if (this.loadedAlarmInstallationsCount >= this.alarmInstallations.length) {
                this.allAlarmInstallationsLoaded = true;
            }
        }, 500);
    }

    loadOkInstallations() {
        this.loadedOkInstallations = this.okInstallations.slice(0, this.loadedOkInstallationsCount);
        if (this.allOkInstallationsLoadedTimeout) {
            clearTimeout(this.allOkInstallationsLoadedTimeout);
        }
        this.allOkInstallationsLoadedTimeout = setTimeout(() => {
            if (this.loadedOkInstallationsCount >= this.okInstallations.length) {
                this.allOkInstallationsLoaded = true;
            }
        }, 500);
    }

    @Watch('search', { deep: true })
    onSearchChange() {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        this.searchTimeout = setTimeout(() => {
            this.reloadInstallation();
        }, 200);
    }

    handleScroll() {
        const alarmInstallations = this.$refs.alarmInstallations;
        const okInstallations = this.$refs.okInstallations;
        if (alarmInstallations && typeof alarmInstallations.getBoundingClientRect === 'function') {
            if (
                alarmInstallations.getBoundingClientRect().bottom < window.innerHeight + 500 &&
                this.loadedAlarmInstallationsCount < this.alarmInstallations.length
            ) {
                this.loadedAlarmInstallationsCount += 10;
                Vue.nextTick(() => {
                    this.loadAlarmInstallations();
                });
            }
        }
        if (okInstallations && typeof okInstallations.getBoundingClientRect === 'function') {
            if (
                okInstallations.getBoundingClientRect().bottom < window.innerHeight + 500 &&
                this.loadedOkInstallationsCount < this.okInstallations.length
            ) {
                this.loadedOkInstallationsCount += 10;
                Vue.nextTick(() => {
                    this.loadOkInstallations();
                });
            }
        }
    }
}
</script>
